import styled from 'styled-components';
import { noop } from 'utils/noop';
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Table } from 'ui/table';
import { TableRows } from 'ui/tableRows';
import { TableHeader } from 'ui/tableHeader';
import { FlexSpacer } from 'ui/flexSpacer';
import { PrimaryButton } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { configurePathsColumns } from './PathsColumns';
import { CreateDeepLinkPathModal } from './CreateDeepLinkPathModal';
import { EnablePathModal } from './EnablePathModal';
import {
  selectIfAssetLinksExist,
  selectTrackingDomainHasActiveLegacyAppSiteAssociation,
} from '../selectors';
import { StyledH3 } from './TrackingDomain.styled';
import {
  CREATE_TRACKING_DOMAIN_PATH,
  ENABLE_TRACKING_DOMAIN_PATH,
  HTTPS_DISABLED_TOOLTIP,
} from '../constants';
// import { AppSiteAssociations, TrackingDomain } from '../types';

const ButtonSpacer = styled.div`
  min-width: fit-content;
`;

const TEXT = {
  header: 'Destination domain',
  description:
    'Destination domains, combined with the paths specified in their apple-app-site-association files, determine the URLs that Iterable will rewrite as deep links (for this tracking domain).',
  createPathCTA: 'New destination path',
};

// type Props = {
//   appAssociationSites: Array<AppSiteAssociations>,
//   trackingDomain: TrackingDomain,
// };

export const Paths = ({ appAssociationSites = [], trackingDomain = {} }) => {
  const history = useHistory();
  const [modalState, setModalState] = useState('');
  const [selectedAppAssociationSite, setSelectedAppAssociationSite] = useState({});

  const closeModal = useCallback(() => setModalState(''), []);

  const androidAssetLinksExist = useSelector(selectIfAssetLinksExist);
  const hasActiveLegacyDomain = useSelector((state) =>
    selectTrackingDomainHasActiveLegacyAppSiteAssociation(state, {
      trackingDomainName: trackingDomain.domain,
    }),
  );

  // if useHttps is false for the domain, paths don't work
  const { useHttps } = trackingDomain;

  const columns = configurePathsColumns({
    enabledForAndroid: androidAssetLinksExist,
    isDisabled: !useHttps,
    onEdit: ({ domain, appDomain }) =>
      history.push(
        `/settings/domains/${encodeURIComponent(domain)}/${encodeURIComponent(appDomain)}`,
      ),
    onEnable: (domain) => {
      setSelectedAppAssociationSite(domain);
      setModalState(ENABLE_TRACKING_DOMAIN_PATH);
    },
  });

  const createModalIsOpen = modalState === CREATE_TRACKING_DOMAIN_PATH;
  const enableModalIsOpen = modalState === ENABLE_TRACKING_DOMAIN_PATH;

  return (
    <section data-test="paths-details-container">
      <FlexSpacer gap="plusHalf">
        <div>
          <StyledH3>{TEXT.header}</StyledH3>
          <p>{TEXT.description}</p>
        </div>
        <ButtonSpacer>
          <Tooltip isEnabled={!useHttps} tooltipContent={HTTPS_DISABLED_TOOLTIP}>
            <PrimaryButton
              data-test="create-path-button"
              isDisabled={!useHttps}
              onClick={() => setModalState(CREATE_TRACKING_DOMAIN_PATH)}
            >
              {TEXT.createPathCTA}
            </PrimaryButton>
          </Tooltip>
        </ButtonSpacer>
      </FlexSpacer>
      <br />
      {appAssociationSites.length > 0 && (
        <Table>
          <TableHeader
            columns={columns}
            sortedBy=""
            sortDirection=""
            setSortDirection={noop}
            setSortedBy={noop}
          />
          <TableRows rows={appAssociationSites} columns={columns} />
        </Table>
      )}
      {createModalIsOpen && (
        <CreateDeepLinkPathModal
          onClose={closeModal}
          trackingDomainName={trackingDomain.domain}
          appAssociationSites={appAssociationSites}
        />
      )}
      {enableModalIsOpen && (
        <EnablePathModal
          hasActiveLegacyDomain={hasActiveLegacyDomain}
          onClose={closeModal}
          selectedAppSiteAssociation={selectedAppAssociationSite}
        />
      )}
    </section>
  );
};
